import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { TransformStraightMove } from "../../transforms/transform_straight_move.js";
import { ColorScheme } from "./color_scheme.js";
import { RoundedRectangle } from "./rounded_rectangle.js";
import { TransformOptions, UIElement } from "./ui_element.js";
import { UIImage } from "./ui_image.js";

export interface SettingsToggleOptions
{
  initial_state?: boolean;
  on_toggle?: ( new_state: boolean ) => void;
}

function hexToRgb( hex: string ): [number, number, number]
{
  const n = parseInt( hex.replace( "#", "" ), 16 );
  return [ ( n >> 16 ) & 255, ( n >> 8 ) & 255, n & 255 ];
}

function lerpColor( from: [number, number, number], to: [number, number, number], t: number ): string
{
  const r = Math.round( from[0] + ( to[0] - from[0] ) * t );
  const g = Math.round( from[1] + ( to[1] - from[1] ) * t );
  const b = Math.round( from[2] + ( to[2] - from[2] ) * t );
  return `rgb(${r},${g},${b})`;
}

// Track image + inset color pill (green when on / red when off, animated
// crossfade) + sliding knob, matching the XD "Sounds"/"Music" toggle.
export class SettingsToggle extends UIElement
{
  private static readonly KNOB_EDGE_PADDING_RATIO = 16 / 152;
  private static readonly PILL_INSET_X_RATIO = 9 / 152;
  private static readonly PILL_INSET_Y_RATIO = 8 / 68;
  private static readonly KNOB_DIAMETER_RATIO = 42 / 152;
  private static readonly PILL_RADIUS_RATIO = 26 / 152;

  // Nudges the pill+knob (not the track image) up by 1px.
  private static readonly PILL_KNOB_Y_OFFSET = 1;

  private static readonly SLIDE_DURATION_MS = 120;

  private static readonly ON_RGB = hexToRgb( ColorScheme.settings_toggle_on );
  private static readonly OFF_RGB = hexToRgb( ColorScheme.settings_toggle_off );

  private state: boolean;
  private on_toggle?: ( new_state: boolean ) => void;
  private knob!: RoundedRectangle;
  private pill!: RoundedRectangle;
  private knob_y = 0;
  private knob_d = 0;

  private color_transition_start = 0;
  private color_transitioning = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: SettingsToggleOptions,
    ui_options?: TransformOptions
  )
  {
    super( ctx, x, y, width, height, false, true, ui_options );

    const { initial_state = false, on_toggle } = options;

    this.state = initial_state;
    this.on_toggle = on_toggle;

    const w = width;
    const h = height;

    // None of these have their own onClick, but their default hit-test would
    // still "catch" the touch and stop it reaching the toggle's own onClick —
    // make them unclickable so the toggle as a whole handles the tap.
    const track = new UIImage(
      ctx, 0, 0, w, h, ImgID.UI_SettingsToggleTrack
    );
    track.setClickable( false );
    this.addChild( track );

    const pill_inset_x = w * SettingsToggle.PILL_INSET_X_RATIO;
    const pill_inset_y = h * SettingsToggle.PILL_INSET_Y_RATIO;
    const pill_w = w - pill_inset_x * 2;
    const pill_h = h - pill_inset_y * 2;

    this.pill = new RoundedRectangle(
      ctx, pill_inset_x, pill_inset_y - SettingsToggle.PILL_KNOB_Y_OFFSET, pill_w, pill_h,
      w * SettingsToggle.PILL_RADIUS_RATIO,
      this.state ? ColorScheme.settings_toggle_on : ColorScheme.settings_toggle_off
    );
    this.pill.setClickable( false );
    this.addChild( this.pill );

    this.knob_d = w * SettingsToggle.KNOB_DIAMETER_RATIO;
    this.knob_y = ( h - this.knob_d ) / 2 - SettingsToggle.PILL_KNOB_Y_OFFSET;

    this.knob = new RoundedRectangle(
      ctx, this.knobX(), this.knob_y, this.knob_d, this.knob_d,
      this.knob_d / 2,
      ColorScheme.settings_toggle_knob
    );
    this.knob.setClickable( false );
    this.addChild( this.knob );

    this.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.state = !this.state;

      this.knob.addTransform( new TransformStraightMove(
        this.knob,
        true,
        null,
        new Vector2( this.knobX(), this.knob_y ),
        performance.now(),
        SettingsToggle.SLIDE_DURATION_MS
      ) );

      this.color_transition_start = performance.now();
      this.color_transitioning = true;

      this.setShouldBeRendered();

      if ( this.on_toggle )
        this.on_toggle( this.state );
    } );
  }

  // Off (red) = knob on the left, On (green) = knob on the right.
  private knobX(): number
  {
    const w = this.rect.getW();
    const padding = w * SettingsToggle.KNOB_EDGE_PADDING_RATIO;

    return this.state ? w - padding - this.knob_d : padding;
  }

  public getState(): boolean
  {
    return this.state;
  }

  protected updateBasic( now: number ): void
  {
    super.updateBasic( now );

    if ( !this.color_transitioning )
      return;

    const t = Math.min( 1, ( now - this.color_transition_start ) / SettingsToggle.SLIDE_DURATION_MS );

    const from = this.state ? SettingsToggle.OFF_RGB : SettingsToggle.ON_RGB;
    const to = this.state ? SettingsToggle.ON_RGB : SettingsToggle.OFF_RGB;

    this.pill.setColor( lerpColor( from, to, t ) );
    this.setShouldBeRendered();

    if ( t >= 1 )
      this.color_transitioning = false;
  }

  protected override syncDomContent( _node: HTMLElement ): boolean
  {
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "SettingsToggle";
  }
}
